import { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Bot,
  CheckSquare,
  LoaderCircle,
  ShieldCheck,
  Sparkles,
  Square,
  X,
} from "lucide-react";

const SCOPES = [
  { key: "jobs", label: "Jobs" },
  { key: "po_vendor", label: "PO & Vendor" },
  { key: "drawings", label: "Drawings" },
];

export default function ClaudeOperationsPanel({ orders }) {
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState("jobs");
  const [focus, setFocus] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [streamedText, setStreamedText] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const abortRef = useRef(null);
  const selectableOrders = useMemo(() => orders.slice(0, 10), [orders]);

  useEffect(() => {
    setSelectedOrderIds((current) => {
      const available = new Set(selectableOrders.map((order) => order.id));
      const retained = current.filter((id) => available.has(id));
      if (retained.length) return retained;
      return selectableOrders.slice(0, 5).map((order) => order.id);
    });
  }, [selectableOrders]);

  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  const selectedCount = selectedOrderIds.length;

  const toggleOrder = (orderId) => {
    setSelectedOrderIds((current) => {
      if (current.includes(orderId)) {
        return current.filter((id) => id !== orderId);
      }
      if (current.length >= 10) return current;
      return [...current, orderId];
    });
  };

  const parseSseEvent = (block) => {
    const event = block
      .split("\n")
      .find((line) => line.startsWith("event:"))
      ?.replace("event:", "")
      .trim();
    const data = block
      .split("\n")
      .find((line) => line.startsWith("data:"))
      ?.replace("data:", "")
      .trim();

    if (!event || !data) return null;
    return { event, payload: JSON.parse(data) };
  };

  const closePanel = () => {
    abortRef.current?.abort();
    setOpen(false);
  };

  const analyze = async () => {
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setAnalysis(null);
    setErrorMessage("");
    setStreamedText("");

    try {
      const token = localStorage.getItem("dvepl_token");
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ai/operations-analysis`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            scope,
            order_ids: selectedOrderIds,
            focus: focus.trim() || undefined,
          }),
          signal: controller.signal,
        },
      );

      if (!response.ok || !response.body) {
        const detail = await response.json().catch(() => ({}));
        throw new Error(detail.detail || "Claude could not start this analysis.");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let completed = false;

      while (!completed) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const blocks = buffer.split("\n\n");
        buffer = blocks.pop() || "";

        blocks.forEach((block) => {
          const message = parseSseEvent(block);
          if (!message) return;
          if (message.event === "token") {
            setStreamedText((current) => current + message.payload.content);
          }
          if (message.event === "complete") {
            completed = true;
            setAnalysis(message.payload.analysis);
          }
          if (message.event === "error") {
            throw new Error(message.payload.detail);
          }
        });
      }

      if (!completed) {
        throw new Error("Claude did not return a complete review brief.");
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        setErrorMessage(error.message || "Claude could not complete this analysis right now.");
      }
    } finally {
      setLoading(false);
      abortRef.current = null;
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="claude-operations-open"
        className="fixed bottom-5 right-5 z-30 inline-flex items-center gap-2 rounded-lg bg-emerald-700 px-4 py-3 text-xs font-extrabold text-white shadow-lg transition-colors hover:bg-emerald-800"
      >
        <Sparkles size={16} /> Claude Operations Brief
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="flex max-h-[90vh] w-[min(96vw,720px)] max-w-none flex-col gap-0 overflow-hidden border-slate-200 bg-white p-0"
          data-testid="claude-operations-panel"
        >
          <div className="flex items-start justify-between bg-emerald-800 px-5 py-4 text-white">
            <div className="flex items-start gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-500 text-white">
                <Bot size={19} />
              </span>
              <div>
                <DialogTitle className="font-heading text-lg font-extrabold">
                  Claude Operations Brief
                </DialogTitle>
                <DialogDescription className="mt-0.5 text-xs text-emerald-100">
                  Read-only analysis and reviewable drafts for live operational context.
                </DialogDescription>
              </div>
            </div>
            <button
              type="button"
            onClick={closePanel}
              data-testid="claude-operations-close"
              className="text-emerald-100 transition-colors hover:text-white"
              aria-label="Close Claude operations brief"
            >
              <X size={19} />
            </button>
          </div>
          <div className="overflow-y-auto p-5">
            <div className="flex flex-wrap gap-2" data-testid="claude-scope-tabs">
              {SCOPES.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => setScope(item.key)}
                  data-testid={`claude-scope-${item.key}`}
                  className={`rounded-md border px-3 py-2 text-xs font-bold transition-colors ${
                    scope === item.key
                      ? "border-emerald-700 bg-emerald-700 text-white"
                      : "border-slate-200 bg-white text-slate-600 hover:border-emerald-300"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <section className="mt-4" data-testid="claude-job-selector">
              <div className="flex items-baseline justify-between gap-3">
                <h3 className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500">
                  Jobs included in this brief
                </h3>
                <span
                  className="shrink-0 text-[10px] font-bold text-emerald-700"
                  data-testid="claude-selected-job-count"
                >
                  {selectedCount} selected
                </span>
              </div>
              <div className="mt-2 max-h-36 divide-y divide-slate-100 overflow-y-auto border border-slate-200 bg-white">
                {!selectableOrders.length && (
                  <p
                    className="px-3 py-3 text-xs text-slate-500"
                    data-testid="claude-no-jobs-available"
                  >
                    No jobs are available to include in a review brief yet.
                  </p>
                )}
                {selectableOrders.map((order) => {
                  const selected = selectedOrderIds.includes(order.id);
                  const activeStage = (order.stages || []).find(
                    (stage) => stage.status === "active",
                  );
                  return (
                    <label
                      key={order.id}
                      className="flex cursor-pointer items-center gap-2 px-3 py-2 text-xs text-slate-700 transition-colors hover:bg-emerald-50"
                    >
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggleOrder(order.id)}
                        data-testid={`claude-job-select-${order.id}`}
                        className="sr-only"
                      />
                      {selected ? (
                        <CheckSquare size={15} className="shrink-0 text-emerald-700" />
                      ) : (
                        <Square size={15} className="shrink-0 text-slate-400" />
                      )}
                      <span className="min-w-0 flex-1 truncate font-semibold">
                        {order.customer || order.name || "Unnamed customer"}
                      </span>
                      <span className="shrink-0 text-[10px] text-slate-500">
                        {order.customer_code || "No ref"}
                      </span>
                      <span className="max-w-28 shrink truncate text-[10px] text-slate-400">
                        {activeStage?.name || "No active stage"}
                      </span>
                    </label>
                  );
                })}
              </div>
            </section>
            <label className="mt-4 block text-[10px] font-extrabold uppercase tracking-wider text-slate-500">
              Review focus (optional)
              <Textarea
                value={focus}
                onChange={(event) => setFocus(event.target.value)}
                placeholder="For example: identify PO commitments most likely to delay production."
                className="mt-1.5 min-h-20 border-slate-200 bg-white text-sm text-slate-800 focus:border-emerald-600 focus-visible:ring-emerald-600"
                data-testid="claude-focus-input"
              />
            </label>
            <div className="mt-3 flex items-center gap-2 text-[11px] text-slate-500">
              <ShieldCheck size={15} className="text-emerald-700" />
              Claude can analyze selected jobs but cannot write, send, assign, or approve anything.
            </div>
            <Button
              type="button"
              onClick={analyze}
              disabled={loading || !selectedCount}
              data-testid="claude-analyze-button"
              className="mt-4 bg-emerald-700 text-white hover:bg-emerald-800"
            >
              {loading ? <LoaderCircle className="animate-spin" size={15} /> : <Sparkles size={15} />}
              {loading ? "Analyzing…" : "Generate review brief"}
            </Button>
            {!!errorMessage && (
              <p
                className="mt-3 border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700"
                data-testid="claude-analysis-error"
              >
                {errorMessage}
              </p>
            )}
            {loading && (
              <p
                className="mt-4 text-sm leading-relaxed text-slate-500"
                data-testid="claude-streaming-preview"
              >
                {streamedText || "Preparing the operational brief…"}
              </p>
            )}
            {analysis && (
              <div className="mt-5 space-y-4 border-t border-slate-200 pt-5" data-testid="claude-analysis-result">
                <section>
                  <h3 className="text-xs font-extrabold uppercase tracking-wider text-emerald-800">
                    Operations summary
                  </h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-700">
                    {analysis.executive_summary || "No summary returned."}
                  </p>
                </section>
                <AnalysisList title="Risks" items={analysis.risks} testId="claude-risks" />
                <AnalysisList
                  title="Recommended actions"
                  items={analysis.recommended_actions}
                  testId="claude-actions"
                />
                {!!analysis.draft_messages?.length && (
                  <section data-testid="claude-drafts">
                    <h3 className="text-xs font-extrabold uppercase tracking-wider text-emerald-800">
                      Reviewable drafts
                    </h3>
                    <div className="mt-2 space-y-2">
                      {analysis.draft_messages.map((draft, index) => (
                        <div
                          key={index}
                          className="border border-slate-200 bg-white p-3 text-sm text-slate-700"
                          data-testid={`claude-draft-${index}`}
                        >
                          <div className="text-[10px] font-extrabold uppercase tracking-wider text-emerald-700">
                            {draft.recipient_role || "Suggested recipient"}
                          </div>
                          <p className="mt-1 leading-relaxed">{draft.message}</p>
                        </div>
                      ))}
                    </div>
                  </section>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function AnalysisList({ title, items, testId }) {
  if (!items?.length) return null;
  return (
    <section data-testid={testId}>
      <h3 className="text-xs font-extrabold uppercase tracking-wider text-emerald-800">{title}</h3>
      <ul className="mt-2 space-y-1.5 text-sm leading-relaxed text-slate-700">
        {items.map((item, index) => (
          <li key={index}>• {item}</li>
        ))}
      </ul>
    </section>
  );
}